import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import Select from "react-select";
import "./StatusReport.css";
import { FaClock } from 'react-icons/fa';
const primary = {
  50: "#f3f1ff",
  100: "#e9e5ff",
  200: "#d5cfff",
  300: "#b7a9ff",
  400: "#9478ff",
  500: "#7341ff",
  600: "#631bff",
  700: "#611bf8",
  800: "#4607d0",
  900: "#3c08aa",
  950: "#220174",
  DEFAULT: "#611bf8",
  baseValue: "#611BF8"
};

const neutral = {
  50: "#f7f7f7",
  100: "#eeeeee",
  200: "#e0e0e0",
  300: "#cacaca",
  400: "#b1b1b1",
  500: "#999999",
  600: "#7f7f7f",
  700: "#676767",
  800: "#545454",
  900: "#464646",
  950: "#282828",
  baseValue: "#FFFFFF"
};


const AverageResolutionTime = () => {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 29);

  const defaultFromDate = formatDate(thirtyDaysAgo);
  const defaultToDate = formatDate(today);

  const [fromDateFilter, setFromDateFilter] = useState(defaultFromDate);
  const [toDateFilter, setToDateFilter] = useState(defaultToDate);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [supportCenterOptions, setSupportCenterOptions] = useState([]);
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [resolutionData, setResolutionData] = useState({
    daily: { categories: [], avgResolutionTimeInHours: [] },
    weekly: { categories: [], avgResolutionTimeInHours: [] },
    monthly: { categories: [], avgResolutionTimeInHours: [] },
    metrics: {
      currentAvg: 0,
      previousAvg: 0,
      improvement: 0,
      target: 1.0,
      topCategories: []
    }
  });
  const [loading, setLoading] = useState(true);


  const fetchResolutionData = async () => {
    setLoading(true);
    const academyIds = selectedCenter?.value;
    const categoryId = selectedCategory?.value;
    const subCategoryId = selectedSubcategory?.value;
    const url = new URL(
      process.env.REACT_APP_API_TICKET_RESOLUTION_REPORT
    );
    const params = url.searchParams;
    params.set("startDate", fromDateFilter);
    params.set("endDate", toDateFilter);
    if (academyIds) params.set("academyIds", academyIds);
    if (categoryId) params.set("categoryId", categoryId);
    if (subCategoryId) params.set("subCategoryId", subCategoryId);

    try {
      const response = await fetch(url.toString(), {
        headers: {
          "Content-Type": "application/json",
          "x-authorization": process.env.REACT_APP_AUTH_TOKEN
        }
      });
      const data = await response.json();

      if (data.success) {

        const {
          daily: rawDaily = {},
          weekly: rawWeekly = {},
          monthly: rawMonthly = {},
          overallAvgResolutionTimeInHours = 0
        } = data.data;

        const transformedData = {
          daily: {
            categories: rawDaily.categories || [],
            avgResolutionTimeInHours: rawDaily.avgResolutionTimeInHours || []
          },
          weekly: {
            categories: rawWeekly.categories || [],
            avgResolutionTimeInHours: rawWeekly.avgResolutionTimeInHours || []
          },
          monthly: {
            categories: rawMonthly.categories || [],
            avgResolutionTimeInHours: rawMonthly.avgResolutionTimeInHours || []
          },
          metrics: {
            currentAvg: overallAvgResolutionTimeInHours,
            previousAvg: 0,
            improvement: 0,
            target: 1.0,
            topCategories: []
          }
        };

        setResolutionData(transformedData);
      }
    } catch (error) {
      console.error("Failed to fetch resolution data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResolutionData();
  }, [fromDateFilter, toDateFilter]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const usersResponse = await fetch(process.env.REACT_APP_API_EMPLOYEE_LIST, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const usersData = await usersResponse.json();
        if (usersData.success) {

        }

        const response = await fetch(process.env.REACT_APP_API_TICKET_DROPDOWN, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const data = await response.json();

        if (data.success) {
          const categoryList = data.data.categories.map(cat => ({
            label: cat.name,
            value: cat.id
          }));

          const centerList = Object.entries(data.data.academies).map(([id, name]) => ({
            label: name,
            value: id
          }));

          const subMap = {};
          data.data.categories.forEach(cat => {
            subMap[cat.id] = cat.subcategories.map(sub => ({
              label: sub.name,
              value: sub.id
            }));
          });
          setCategoryOptions(categoryList);
          setSupportCenterOptions(centerList);
          setSubcategoriesMap(subMap);
        }
      } catch (error) {
        console.error("Dropdown fetch failed:", error);
      }
    };

    fetchDropdownData();
  }, []);
  const handleFromDateChange = (e) => {
    setFromDateFilter(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDateFilter(e.target.value);
  };

  const calculateDayDifference = () => {
    const from = new Date(fromDateFilter);
    const to = new Date(toDateFilter);
    const diffTime = Math.abs(to - from);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const getViewType = () => {
    const dayDiff = calculateDayDifference();
    if (dayDiff <= 31) return "day";
    if (dayDiff <= 95) return "week";
    return "month";
  };

  const generateCategories = () => {
    const type = getViewType();
    if (type === "day") return resolutionData.daily.categories;
    if (type === "week") return resolutionData.weekly.categories;
    return resolutionData.monthly.categories;
  };

  const generateSeriesData = () => {
    const type = getViewType();
    let data;

    if (type === "day") {
      data = resolutionData.daily.avgResolutionTimeInHours || [];
    } else if (type === "week") {
      data = resolutionData.weekly.avgResolutionTimeInHours || [];
    } else {
      data = resolutionData.monthly.avgResolutionTimeInHours || [];
    }

    const processedData = data.map(v => {
      const num = Number(v);
      return isNaN(num) ? null : num;
    });

    return [
      {
        name: "Average Resolution Time (hours)",
        data: processedData
      }
    ];
  };

  const seriesData = generateSeriesData();
  const hasChartData = seriesData[0].data.length > 0 && seriesData[0].data.some(v => v !== null);
  const calculateMaxValue = () => {
    const dailyData = resolutionData.daily.avgResolutionTimeInHours || [];
    const weeklyData = resolutionData.weekly.avgResolutionTimeInHours || [];
    const monthlyData = resolutionData.monthly.avgResolutionTimeInHours || [];

    const dailyMax = dailyData.length ? Math.max(...dailyData.filter(v => v)) : 0;
    const weeklyMax = weeklyData.length ? Math.max(...weeklyData.filter(v => v)) : 0;
    const monthlyMax = monthlyData.length ? Math.max(...monthlyData.filter(v => v)) : 0;

    const overallMax = Math.max(dailyMax, weeklyMax, monthlyMax);
    return overallMax > 0 ? overallMax * 1.1 : 10; // Add 10% padding
  };

  const chartOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'Inter, sans-serif',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3, connectNulls: true },
    colors: [primary[600]],
    fill: {
      type: "gradient",
      gradient: {
        shade: 'dark',
        gradientToColors: [primary[400]],
        shadeIntensity: 1,
        type: 'vertical',
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    markers: {
      size: 5,
      colors: [primary[600]],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 7
      }
    },
    xaxis: {
      categories: Array.isArray(generateCategories()) ? generateCategories() : [],
      labels: {
        style: { colors: neutral[600], fontSize: '12px' },
        rotate: generateCategories().length > 15 ? -45 : 0
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: {
        enabled: false
      }
    },
    yaxis: {
      labels: {
        style: { colors: neutral[600], fontSize: '12px' },
        formatter: (value) => `${value}h`
      },
      min: 0,
      max: calculateMaxValue(),
      tickAmount: 5
    },
    tooltip: {
      style: { fontFamily: 'Inter, sans-serif' },
      y: {
        formatter: (value) => `${value} hours`
      }
    },
    grid: {
      borderColor: neutral[200],
      strokeDashArray: 4,
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: {
          height: 300
        },
        xaxis: {
          labels: {
            rotate: -45
          }
        }
      }
    }]
  };
  const avg = resolutionData.metrics.currentAvg;
  const accentColor = '#631bff';
  const formatHoursToDays = (hours) => {
    if (hours === null || hours === undefined) return "-";
  
    const totalHours = Math.floor(hours);
    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;
  
    if (days > 0) {
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    } else {
      return `${remainingHours}h`;
    }
  };
  
  
  return (
    <div className="status-report-container">
      <div className="sales-filters-login">
        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy has-value`}
            value={fromDateFilter}
            onChange={handleFromDateChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          <label htmlFor="fromDateFilter">From Date</label>
        </div>

        <div className="floating-select">
          <input
            type="date"
            className={`sales-filter-academy has-value`}
            value={toDateFilter}
            onChange={handleToDateChange}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          <label htmlFor="toDateFilter">To Date</label>
        </div>

        {/* Category Dropdowns */}
        <div className="floating-select">
          <Select
            name="category"
            options={categoryOptions}
            value={selectedCategory}
            onChange={(selected) => {
              setSelectedCategory(selected);
              setSelectedSubcategory(null);
            }}
            classNamePrefix="react-select"
            className={`sales-filter-court-booking ${selectedCategory ? "has-value" : ""}`}
            placeholder="Choose Category"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {selectedCategory && <label>Category</label>}
        </div>

        <div className="floating-select">
          <Select
            name="subcategory"
            options={
              selectedCategory?.value && subcategoriesMap[selectedCategory.value]
                ? subcategoriesMap[selectedCategory.value]
                : []
            }
            value={selectedSubcategory}
            onChange={setSelectedSubcategory}
            classNamePrefix="react-select"
            className={`sales-filter-court-booking ${selectedSubcategory ? "has-value" : ""}`}
            placeholder="Subcategory"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
          />
          {selectedSubcategory && <label>Subcategory</label>}
        </div>

        <div className="floating-select">
          <Select
            name="supportCenter"
            options={supportCenterOptions}
            value={selectedCenter}
            onChange={setSelectedCenter}
            classNamePrefix="react-select"
            className={`sales-filter-court-booking ${selectedCenter ? "has-value" : ""}`}
            placeholder="Center"
            isClearable
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
            menuPortalTarget={document.body}
          />
          {selectedCenter && <label>Center</label>}
        </div>
        <div className="floating-select">
          <button className="analytics-apply-button" onClick={fetchResolutionData}  >
            <span className="material-symbols-outlined filter-action-icon">filter_alt</span>
            Apply Filters
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="str-insight-hub-grid">
        <div
          className="str-avg-strip"
          style={{ borderTop: `4px solid ${accentColor}` }}
        >
          <div className="str-avg-header">
            <FaClock className="str-avg-icon" style={{ color: accentColor }} />
            <span className="str-avg-label">Current Average</span>
          </div>
          <div className="str-avg-value">{formatHoursToDays(avg)}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="sr-charts-grid grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="sr-trend-chart bg-white rounded-xl shadow-sm p-6 lg:col-span-2 hover:shadow-md transition-shadow border border-gray-100">
          <div className="sr-chart-header flex justify-between items-center mb-6">
            <h2 className="sr-chart-title text-lg font-semibold text-gray-900">Resolution Time Trend</h2>
            <div className="view-type-indicator">
              Showing: {getViewType().toUpperCase()} view
            </div>
          </div>
          <div className="sr-chart-container h-[350px]">
            {!loading && hasChartData && (
              <ReactApexChart
                type="area"
                height={350}
                series={seriesData}
                options={chartOptions}
              />
            )}
            {!loading && !hasChartData && (
              <div className="no-data-message">No resolution time data available</div>
            )}
          </div>
        </div>

        {/* Top Categories Chart - Only show if we have data */}
        {resolutionData.metrics.topCategories.length > 0 && (
          <div className="sr-categories-chart bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow border border-gray-100">
            <div className="sr-chart-header mb-6">
              <h2 className="sr-chart-title text-lg font-semibold text-gray-900">Resolution Time by Category</h2>
            </div>
            <div className="sr-chart-container h-[350px]">
              <ReactApexChart
                type="bar"
                height={350}
                series={[{
                  name: "Resolution Time (hours)",
                  data: resolutionData.metrics.topCategories.map(cat => cat.time)
                }]}
                options={{
                  chart: {
                    toolbar: { show: false }
                  },
                  xaxis: {
                    categories: resolutionData.metrics.topCategories.map(cat => cat.name)
                  },
                  yaxis: {
                    title: {
                      text: "Hours"
                    }
                  },
                  colors: [primary[600]]
                }}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AverageResolutionTime;